import { Component, inject, output, ViewChild } from "@angular/core";
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { IPlannedAssignmentTeamHistory } from "@ramudden/models/planned-event";
import { ModalComponent } from "@ramudden/ui";
import moment from "moment";

interface DateForm {
    teamArrival: FormControl<string>;
    teamDeparture: FormControl<string>;
}

@Component({
    selector: "app-end-site-dialog",
    standalone: true,
    templateUrl: "./end-site-dialog.component.html",
    imports: [ModalComponent, ReactiveFormsModule, TranslateModule],
})
export class EndSiteDialogComponent {
    @ViewChild("modal") modal: ModalComponent;

    private readonly formBuilder = inject(FormBuilder);

    dateForm: FormGroup<DateForm>;

    submitForm = output<IPlannedAssignmentTeamHistory>();

    openModal(dates: IPlannedAssignmentTeamHistory) {
        this.dateForm = this.formBuilder.group<DateForm>(
            {
                teamArrival: new FormControl(moment(dates.teamArrival).format("YYYY-MM-DDTHH:mm"), {
                    nonNullable: true,
                }),
                teamDeparture: new FormControl(moment(dates.teamDeparture).format("YYYY-MM-DDTHH:mm"), {
                    nonNullable: true,
                }),
            },
            {
                validators: [this.dateRangeValidator()],
            },
        );

        this.modal.openModal();
    }

    onSubmit() {
        this.submitForm.emit({
            teamDeparture: moment(this.dateForm.value.teamDeparture).toDate(),
            teamArrival: moment(this.dateForm.value.teamArrival).toDate(),
        });
        this.modal.closeModal();
    }

    //region Helper
    private dateRangeValidator(): ValidatorFn {
        return (formGroup: FormGroup<DateForm>): ValidationErrors | null => {
            const arrivalControl = formGroup.controls.teamArrival;
            const arrivalValue = arrivalControl.value;

            const departureControl = formGroup.controls.teamDeparture;
            const departureValue = departureControl.value;

            if (!arrivalValue || !departureValue) {
                return null;
            }

            const arrival = moment(arrivalValue);
            const departure = moment(departureValue);

            const isArrivalBeforeDeparture = arrival.isBefore(departure);
            const isDepartureAfterArrival = departure.isAfter(arrival);

            if (!isArrivalBeforeDeparture || !isDepartureAfterArrival) {
                // Set specific errors on both controls
                if (!isArrivalBeforeDeparture) {
                    arrivalControl.setErrors({
                        ...arrivalControl.errors,
                        arrivalNotBeforeDeparture: true,
                    });
                } else {
                    // Clear arrival error if it's valid
                    if (arrivalControl.errors) {
                        delete arrivalControl.errors["arrivalNotBeforeDeparture"];
                        if (Object.keys(arrivalControl.errors).length === 0) {
                            arrivalControl.setErrors(null);
                        }
                    }
                }

                if (!isDepartureAfterArrival) {
                    departureControl.setErrors({
                        ...departureControl.errors,
                        departureNotAfterArrival: true,
                    });
                } else {
                    // Clear departure error if it's valid
                    if (departureControl.errors) {
                        delete departureControl.errors["departureNotAfterArrival"];
                        if (Object.keys(departureControl.errors).length === 0) {
                            departureControl.setErrors(null);
                        }
                    }
                }

                return { dateRangeInvalid: true };
            } else {
                // Clear both errors if dates are valid
                if (arrivalControl.errors) {
                    delete arrivalControl.errors["arrivalNotBeforeDeparture"];
                    if (Object.keys(arrivalControl.errors).length === 0) {
                        arrivalControl.setErrors(null);
                    }
                }

                if (departureControl.errors) {
                    delete departureControl.errors["departureNotAfterArrival"];
                    if (Object.keys(departureControl.errors).length === 0) {
                        departureControl.setErrors(null);
                    }
                }

                return null;
            }
        };
    }
    //endregion
}
